import React, { useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import { localFileToBase64 } from "../../modules/playEngineAssetUtils";
import { optimizeImage } from "../../modules/imageOptimizerApi";
import { buildZipBytes, dataUrlToBytes } from "../../modules/zipArchive";

const IMAGE_ACCEPT = ".png,.jpg,.jpeg";

function formatBytes(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function downloadDataUrl(source, fileName) {
  const link = document.createElement("a");
  link.href = source;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function downloadAllAsZip(items) {
  const entries = items
    .map((item) => ({ path: item.file.name, bytes: dataUrlToBytes(item.source) }))
    .filter((entry) => entry.bytes);
  if (!entries.length) return;

  const link = document.createElement("a");
  link.href = URL.createObjectURL(new Blob([buildZipBytes(entries)], { type: "application/zip" }));
  link.download = "optimized-images.zip";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

export default function DialogImageOptimizer({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);

  const completed = useMemo(() => items.filter((item) => item.status === "done"), [items]);

  function addFiles(fileList) {
    const files = Array.from(fileList || []).filter((file) =>
      /\.(png|jpe?g)$/i.test(file.name)
    );
    if (!files.length) return;

    setItems((current) => [
      ...current,
      ...files.map((file) => ({
        id: `${file.name}-${file.lastModified}-${Math.random()}`,
        file,
        status: "queued",
      })),
    ]);
  }

  async function optimizeAll() {
    const queued = items.filter((item) => item.status === "queued");
    if (!queued.length || isOptimizing) return;
    setIsOptimizing(true);

    for (const item of queued) {
      setItems((current) => current.map((entry) =>
        entry.id === item.id ? { ...entry, status: "optimizing" } : entry
      ));
      try {
        const source = await localFileToBase64(item.file);
        const result = await optimizeImage(source, item.file.name);
        setItems((current) => current.map((entry) =>
          entry.id === item.id
            ? { ...entry, status: "done", source: result.source, stats: result }
            : entry
        ));
      } catch (error) {
        setItems((current) => current.map((entry) =>
          entry.id === item.id ? { ...entry, status: "error", error: error.message } : entry
        ));
      }
    }
    setIsOptimizing(false);
  }

  function reset() {
    if (isOptimizing) return;
    setItems([]);
    setIsDragging(false);
  }

  return (
    <Dialog open={open} onClose={isOptimizing ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Image Optimizer</DialogTitle>
      <DialogContent>
        <div
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setIsDragging(false);
            addFiles(event.dataTransfer.files);
          }}
          style={{
            border: `2px dashed ${isDragging ? "#90caf9" : "#888"}`,
            borderRadius: 6,
            padding: "30px 16px",
            textAlign: "center",
            background: isDragging ? "rgba(144, 202, 249, 0.10)" : "transparent",
          }}
        >
          <Typography variant="h6">Drop your images here</Typography>
          <Typography variant="body2" style={{ margin: "6px 0 14px", color: "#777" }}>
            PNG and JPEG files, one or many at a time
          </Typography>
          <Button variant="outlined" component="label" disabled={isOptimizing}>
            Choose images
            <input
              type="file"
              hidden
              multiple
              accept={IMAGE_ACCEPT}
              onChange={(event) => {
                addFiles(event.target.files);
                event.target.value = "";
              }}
            />
          </Button>
        </div>

        {items.length > 0 && (
          <div style={{ marginTop: 16 }}>
            {items.map((item) => {
              const originalBytes = item.stats?.originalSize || item.file.size;
              const optimizedBytes = item.stats?.compressedSize;
              return (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "9px 0",
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap>{item.file.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {item.status === "optimizing" && "Optimizing…"}
                      {item.status === "queued" && `${formatBytes(item.file.size)} — Waiting`}
                      {item.status === "done" && `${formatBytes(originalBytes)} → ${formatBytes(optimizedBytes)} (${item.stats.savedPercent}% smaller)`}
                      {item.status === "error" && `Failed: ${item.error}`}
                    </Typography>
                  </div>
                  {item.status === "optimizing" && <CircularProgress size={20} />}
                  {item.status === "done" && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => downloadDataUrl(item.source, item.file.name)}
                    >
                      Download
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {completed.length > 0 && (
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 12 }}>
            Optimized files are downloaded individually; your original files are not changed.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={reset} disabled={isOptimizing || !items.length}>Clear</Button>
        <Button onClick={onClose} disabled={isOptimizing}>Close</Button>
        <Button
          onClick={() => downloadAllAsZip(completed)}
          variant="outlined"
          disabled={isOptimizing || !completed.length}
        >
          Download all
        </Button>
        <Button onClick={optimizeAll} color="primary" variant="contained" disabled={isOptimizing || !items.some((item) => item.status === "queued")}>
          {isOptimizing ? "Optimizing…" : "Optimize images"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
